<template>
  <div class="specializations-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <h1>{{ dt('specializations.title') }}</h1>
        <p class="hero-subtitle">{{ dt('specializations.subtitle') }}</p>
        
        <!-- Search Box -->
        <div class="search-box">
          <input 
            v-model="searchQuery" 
            type="text" 
            :placeholder="dt('specializations.searchPlaceholder')"
            @input="filterSpecializations"
          />
          <span class="search-icon">🔍</span>
        </div>
      </div>
    </section>

    <!-- Categories -->
    <section class="categories-section">
      <div class="container">
        <!-- Quick Navigation -->
        <div class="quick-nav">
          <span class="nav-label">{{ dt('specializations.jumpTo') }}:</span>
          <button 
            v-for="category in categories" 
            :key="category.name"
            @click="scrollToCategory(category.name)"
            class="nav-btn"
          >
            {{ getLocalizedLabel(category, currentLocale) }}
          </button>
        </div>

        <!-- Category Accordions -->
        <div 
          v-for="category in filteredCategories" 
          :key="category.name"
          :id="slugify(category.name)"
          class="category-card"
        >
          <div 
            class="category-header"
            @click="toggleCategory(category.name)"
          >
            <div class="category-info">
              <h2>{{ getLocalizedLabel(category, currentLocale) }}</h2>
              <!-- Fixed: Pass the category object, not just description string -->
              <p class="category-description">{{ getLocalizedDescription(category, currentLocale) }}</p>
              <span class="spec-count">{{ category.specializations.length }} {{ dt('specializations.specializationsText') }}</span>
            </div>
            <span class="toggle-icon" :class="{ 'open': openCategories.includes(category.name) }">
              ▼
            </span>
          </div>
          
          <div 
            class="category-content"
            :class="{ 'open': openCategories.includes(category.name) }"
          >
            <div class="specializations-grid">
              <div 
                v-for="spec in category.specializations" 
                :key="spec.value"
                class="specialization-card"
                @click="searchBySpecialization(spec)"
              >
                <h3>{{ getLocalizedLabel(spec, currentLocale) }}</h3>
                <!-- Fixed: Use getLocalizedDescription function -->
                <p>{{ getLocalizedDescription(spec, currentLocale) }}</p>
                <span class="find-btn">{{ dt('specializations.findProviders') }} →</span>
              </div>
            </div>
          </div>
        </div>

        <!-- No Results -->
        <div v-if="filteredCategories.length === 0" class="no-results">
          <p>{{ dt('specializations.noResults', { query: searchQuery }) }}</p>
          <button @click="clearSearch" class="clear-btn">{{ dt('specializations.clearSearch') }}</button>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>{{ dt('specializations.cta.title') }}</h2>
        <p>{{ dt('specializations.cta.subtitle') }}</p>
        <router-link to="/register?role=provider" class="cta-btn">{{ dt('specializations.cta.button') }}</router-link>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import specializationsData from '@/data/specializations.json'
import { dt, getCurrentLocaleCode, getLocalizedLabel, getLocalizedDescription } from '@/utils/i18n'
import { getCurrentLanguage } from '@/services/language'

const router = useRouter()
const { locale } = useI18n()
const searchQuery = ref('')
const openCategories = ref([])
const categories = ref(specializationsData.categories)
const selectedLanguage = ref(getCurrentLanguage())

// Get current locale code reactively (same as homepage)
const currentLocale = computed(() => getCurrentLocaleCode())

// Open first category by default
onMounted(() => {
  if (categories.value.length > 0) {
    openCategories.value.push(categories.value[0].name)
  }
})

const filteredCategories = computed(() => {
  if (!searchQuery.value.trim()) {
    return categories.value
  }
  
  const query = searchQuery.value.toLowerCase()
  
  return categories.value
    .map(category => ({
      ...category,
      specializations: category.specializations.filter(spec => {
        const localizedLabel = getLocalizedLabel(spec, currentLocale.value).toLowerCase()
        const englishLabel = spec.label.toLowerCase()
        // Fixed: Use getLocalizedDescription function
        const localizedDescription = getLocalizedDescription(spec, currentLocale.value).toLowerCase()
        const englishDescription = spec.description.toLowerCase()
        const value = spec.value.toLowerCase()
        
        return localizedLabel.includes(query) ||
               englishLabel.includes(query) ||
               localizedDescription.includes(query) ||
               englishDescription.includes(query) ||
               value.includes(query)
      })
    }))
    .filter(category => category.specializations.length > 0)
})

const toggleCategory = (categoryName) => {
  const index = openCategories.value.indexOf(categoryName)
  if (index > -1) {
    openCategories.value.splice(index, 1)
  } else {
    openCategories.value.push(categoryName)
  }
}

const slugify = (text) => {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

const scrollToCategory = (categoryName) => {
  const element = document.getElementById(slugify(categoryName))
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // Open the category
    if (!openCategories.value.includes(categoryName)) {
      openCategories.value.push(categoryName)
    }
  }
}

const searchBySpecialization = (spec) => {
  router.push({ 
    path: '/search', 
    query: { 
      q: getLocalizedLabel(spec, currentLocale.value),
      language: selectedLanguage.value
    } 
  })
}

const clearSearch = () => {
  searchQuery.value = ''
}

const filterSpecializations = () => {
  // This function is called but the actual filtering is handled by the computed property
}
</script>

<style scoped>
.specializations-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  padding: 60px 20px;
  text-align: center;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 12px;
}

.hero-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 30px;
}

.search-box {
  position: relative;
  max-width: 500px;
  margin: 0 auto;
}

.search-box input {
  width: 100%;
  padding: 16px 20px 16px 50px;
  font-size: 1rem;
  border: none;
  border-radius: 12px;
  outline: none;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
}

/* Quick Navigation */
.quick-nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 20px 0;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.nav-label {
  font-weight: 600;
  color: #475569;
}

.nav-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-btn:hover {
  background: #059669;
  color: white;
  border-color: #059669;
}

/* Categories Section */
.categories-section {
  padding: 40px 20px;
}

.category-card {
  background: white;
  border-radius: 16px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  cursor: pointer;
  transition: background 0.2s;
}

.category-header:hover {
  background: #f8fafc;
}

.category-info h2 {
  font-size: 1.4rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.category-description {
  color: #64748b;
  font-size: 0.95rem;
  margin-bottom: 8px;
}

.spec-count {
  font-size: 0.85rem;
  color: #059669;
  font-weight: 500;
}

.toggle-icon {
  font-size: 1rem;
  color: #94a3b8;
  transition: transform 0.3s;
}

.toggle-icon.open {
  transform: rotate(180deg);
}

.category-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease-out;
}

.category-content.open {
  max-height: 3000px;
}

.specializations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  padding: 0 24px 24px;
}

.specialization-card {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.specialization-card:hover {
  background: #ecfdf5;
  border-color: #059669;
  transform: translateY(-2px);
}

.specialization-card h3 {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.specialization-card p {
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 12px;
}

.find-btn {
  font-size: 0.85rem;
  color: #059669;
  font-weight: 500;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.clear-btn {
  margin-top: 16px;
  padding: 10px 24px;
  background: #059669;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

/* CTA Section */
.cta-section {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: white;
  text-align: center;
  padding: 60px 20px;
}

.cta-section h2 {
  font-size: 1.8rem;
  margin-bottom: 12px;
}

.cta-section p {
  opacity: 0.9;
  margin-bottom: 24px;
}

.cta-btn {
  display: inline-block;
  padding: 14px 32px;
  background: #059669;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: background 0.2s;
}

.cta-btn:hover {
  background: #047857;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .hero h1 {
    font-size: 1.8rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .quick-nav {
    justify-content: center;
  }
  
  .nav-label {
    width: 100%;
    text-align: center;
    margin-bottom: 8px;
  }
  
  .category-info h2 {
    font-size: 1.2rem;
  }
  
  .specializations-grid {
    grid-template-columns: 1fr;
  }
}
</style>